import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  TextField,
  Typography,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@material-ui/core';
import HistoryIcon from '@material-ui/icons/History';
import { useParams } from 'react-router-dom';
import * as contentService from '@api/content/content.api';
import { ContentLogParams } from '@api/content/content-api.types';
import { filterLogs, groupAndSortLogs } from './utils';
import { ColoredChip } from '@components/colored-elements';
import { blue, green, grey, purple } from '@material-ui/core/colors';
import styled from 'styled-components';
import { theme } from '@global';
import { getContentLogUserId } from '@api/content/content.api';

// ---------------- Types ----------------
interface ChangeLog {
  id: number;
  entity: string;
  vtr_id: string;
  column_name: string;
  reason_for_change?: string;
  old_value: string;
  new_value: string;
  username: string;
  user_id: string;
  timestamp: string;
  update_method?: string;
}

interface FilterLogResponse {
  data: {
    data: ChangeLog[];
    total: number;
  };
}

// ---------------- Component ----------------
const VersionHistoryTable: React.FC = () => {
  const { id: vtr_id } = useParams<{ id: string }>(); // fix type

  const [logs, setLogs] = useState<ChangeLog[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [userFilter, setUserFilter] = useState('');
  const [columnFilter, setColumnFilter] = useState('');
  const [userOptions, setUserOptions] = useState<string[]>([]);
  const [columnOptions, setColumnOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!vtr_id) return;
    // Fetch user IDs
    const userSub = getContentLogUserId(vtr_id).subscribe({
      next: (users: string[]) => {
        setUserOptions(users);
      },
      error: err => console.error('Error loading user IDs:', err),
    });

    // Fetch columns
    const columnSub = contentService.getContentColumnName().subscribe({
      next: (columns: string[]) => {
        setColumnOptions(columns);
      },
      error: err => console.error('Error loading columns:', err),
    });
    return () => {
      userSub.unsubscribe();
      columnSub.unsubscribe();
    };
  }, [vtr_id]);

  useEffect(() => {
    if (!vtr_id) return;
    // show loader when fetching
    setLoading(true);
    setError(null);
    const params: ContentLogParams = {
      vtr_id,
      page: page + 1,     // ✅ FIX HERE  
      page_size: rowsPerPage,
      ...(userFilter ? { user_id: userFilter } : {}),
      ...(columnFilter ? { column_name: columnFilter } : {}),
    };

    const subscription = contentService.getContentLogs(params).subscribe({
      next: res => {
        setLogs(res?.data?.data || []);
        setTotalCount(res?.data?.count || 0);
        setLoading(false);
      },
      error: err => {
        console.error('Error fetching logs:', err);
        setError('Failed to load logs');
        setLoading(false);
      },
    });
    // cleanup on unmount
    return () => {
      subscription.unsubscribe();
    };
  }, [vtr_id, userFilter, columnFilter, page, rowsPerPage]);

 // const filteredLogs = filterLogs(logs, userFilter, columnFilter);
  const { flattened } = groupAndSortLogs(logs);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Paper style={{ padding: 16 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" mr={2}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            bgcolor="#1976d2"
            color="#fff"
            borderRadius="4px"
            width={32}
            height={32}
            mr={1}>
            <HistoryIcon fontSize="small" />
          </Box>
          <Box>
            <Typography variant="h5" style={{ fontWeight: 400 }}>
              Version History
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Change logs and edit history of content data
            </Typography>
          </Box>
        </Box>
      </Box>

      <Divider />
      <br />
      {/* Filters */}
      <Box display="flex" mb={2} mr={2} gridGap={'32px'}>
        {/* User Filter */}
        <FormControl size="small" style={{ minWidth: 200 }}>
          <InputLabel>User ID</InputLabel>
          <Select
            label="User ID"
            value={userFilter}
            onChange={e => setUserFilter(e?.target?.value as string)}>
            <MenuItem value="">All</MenuItem>
            {userOptions.map(userId => (
              <MenuItem key={userId} value={userId}>
                {userId}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Column Filter */}
        <FormControl size="small" style={{ minWidth: 200 }}>
          <InputLabel>Column Name</InputLabel>
          <Select
            label="Column Name"
            value={columnFilter}
            onChange={e => setColumnFilter(e.target.value as string)}>
            <MenuItem value="">All</MenuItem>
            {columnOptions.map(col => (
              <MenuItem key={col} value={col}>
                {col}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Loading / Error / Empty */}
      {loading && (
        <Typography color="textSecondary" style={{ marginBottom: 8 }}>
          Loading logs...
        </Typography>
      )}
      {error && (
        <Typography color="error" style={{ marginBottom: 8 }}>
          {error}
        </Typography>
      )}
      {!loading && !error && logs.length === 0 && (
        <Typography color="textSecondary" align="center" style={{ padding: '16px 0' }}>
          No Logs Found
        </Typography>
      )}

      {/* Table */}
      {logs.length > 0 && (
        <>
          <StyledTableContainer>
            <Table>
              <StyledTableHead>
                <TableRow>
                  <TableCell>Date & Time</TableCell>
                  <TableCell>Column Name</TableCell>
                  <TableCell>Old Value</TableCell>
                  <TableCell>New Value</TableCell>
                  <TableCell>User ID</TableCell>
                  <TableCell>Username</TableCell>
                </TableRow>
              </StyledTableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" style={{ padding: '32px' }}>
                      <Typography variant="body2" color="textSecondary">
                        Loading logs...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" style={{ padding: '32px' }}>
                      <Typography variant="body2" color="textSecondary">
                        No Logs Found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  flattened.map(row => (
                    <React.Fragment key={row.id}>
                      {row?.groupDate && (
                        <StyledDateRow>
                          <TableCell colSpan={7}>
                            <StyledDateText>{row?.groupDate}</StyledDateText>
                          </TableCell>
                        </StyledDateRow>
                      )}
                      <StyledTableRow>
                        <StyledCell>{row?.time}</StyledCell>
                        <StyledCell style={{ color: '#283593' }}>{row?.column_name}</StyledCell>
                        <StyledCell style={{ color: purple[600] }}>
                          {row?.old_value || '--'}
                        </StyledCell>
                        <StyledCell style={{ color: green[700] }}>
                          {row?.new_value || '--'}
                        </StyledCell>
                        <StyledCell>
                          <ColoredChip
                            colored={blue[300]}
                            size="small"
                            label={row?.user_id}
                            sx={{
                              borderRadius: 1,
                              fontWeight: 500,
                            }}
                          />
                        </StyledCell>
                        <StyledCell style={{ color: grey[700] }}>
                          {row?.username?.split('@')[0] || '--'}
                        </StyledCell>
                      </StyledTableRow>
                    </React.Fragment>
                  ))
                )}
              </TableBody>
            </Table>
          </StyledTableContainer>

          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onChangePage={handleChangePage}
            rowsPerPage={rowsPerPage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </>
      )}
    </Paper>
  );
};

// 🎨 Styled Components
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: 12,
  overflow: 'hidden',
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  backgroundColor: theme.palette.background.paper,
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  backgroundColor: '#E8F0FE',
  '& th': {
    fontWeight: 600,
    color: '#1A237E',
    fontSize: 14,
    borderBottom: `2px solid ${blue[100]}`,
  },
}));

const StyledDateRow = styled(TableRow)(({ theme }) => ({
  backgroundColor: '#FFF3E0', // pastel orange
  '& td': {
    padding: '6px 16px',
  },
}));

const StyledDateText = styled(Typography)(() => ({
  fontWeight: 600,
  color: '#E65100',
  fontSize: 14,
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  backgroundColor: '#FAFAFA',
  transition: 'background-color 0.3s ease',
  '&:hover': {
    backgroundColor: '#F1F8E9', // light green pastel on hover
  },
}));

const StyledCell = styled(TableCell)(({ theme }) => ({
  fontSize: 13,
  color: grey[800],
  borderBottom: `1px solid ${grey[200]}`,
}));
export default VersionHistoryTable;
